/**
 * Recipe API Service
 *
 * With Better Auth, session cookies are sent automatically via credentials: 'include'.
 * No token parameter needed.
 */

import { apiFetch } from "./client";
import type {
  Recipe,
  RecipeFilters,
  RecipesResponse,
  TagCountsResponse,
} from "./types";
import type {
  RecipeCreateInput,
  RecipeUpdateInput,
} from "@batch-and-bowl/validators";

export type RecipeCollectionFilters = RecipeFilters & {
  mine?: boolean;
  status?: "draft" | "published";
};

/**
 * Fetch all recipes with optional filters (returns response with pagination)
 */
export async function getRecipesWithPagination(
  filters?: RecipeCollectionFilters,
): Promise<RecipesResponse> {
  const params = new URLSearchParams();

  if (filters?.cuisine) params.append("cuisine", filters.cuisine);
  if (filters?.difficulty) params.append("difficulty", filters.difficulty);
  if (filters?.mealType) params.append("mealType", filters.mealType);
  if (filters?.maxTotalTime)
    params.append("maxTotalTime", filters.maxTotalTime.toString());
  if (filters?.search) params.append("search", filters.search);
  if (filters?.page) params.append("page", filters.page.toString());
  if (filters?.limit) params.append("limit", filters.limit.toString());
  if (filters?.tags) {
    filters.tags.forEach((tag) => params.append("tags", tag));
  }
  if (filters?.protein) {
    filters.protein.forEach((value) => params.append("protein", value));
  }
  if (filters?.mine) params.append("mine", "true");
  if (filters?.status) params.append("status", filters.status);

  const queryString = params.toString();
  const endpoint = `/recipes${queryString ? `?${queryString}` : ""}`;

  return apiFetch<RecipesResponse>(endpoint);
}

/**
 * Fetch all recipes with optional filters (legacy - returns just array)
 */
export async function getRecipes(filters?: RecipeFilters): Promise<Recipe[]> {
  const response = await getRecipesWithPagination(filters);
  return response.recipes || [];
}

/**
 * Fetch a single recipe by ID
 */
export async function getRecipe(id: string, proteinChoiceId?: string | null): Promise<Recipe> {
  const query = proteinChoiceId ? `?proteinChoiceId=${encodeURIComponent(proteinChoiceId)}` : "";
  return apiFetch<Recipe>(`/recipes/${id}${query}`);
}

/**
 * Toggle recipe favorite status
 */
export async function toggleRecipeFavorite(
  recipeId: string,
): Promise<{ favorited: boolean }> {
  return apiFetch<{ favorited: boolean }>(`/recipes/favorites/${recipeId}`, {
    method: "POST",
  });
}

/**
 * Get the caller's favorite recipes. scope="household" returns the union
 * of favorites across the caller's household with per-recipe attribution
 * (Recipe.favoritedBy); default "mine" stays byte-identical to the
 * pre-household response.
 */
export async function getFavoriteRecipes(
  scope: "mine" | "household" = "mine",
): Promise<Recipe[]> {
  const qs = scope === "household" ? "?scope=household" : "";
  return apiFetch<Recipe[]>(`/recipes/favorites${qs}`);
}

/**
 * Create a new recipe
 */
export async function createRecipe(data: RecipeCreateInput): Promise<Recipe> {
  return apiFetch<Recipe>("/recipes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing recipe
 */
export async function updateRecipe(
  id: string,
  data: RecipeUpdateInput,
): Promise<Recipe> {
  return apiFetch<Recipe>(`/recipes/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/**
 * Publish a recipe (change status from draft to published)
 */
export async function publishRecipe(id: string): Promise<Recipe> {
  return apiFetch<Recipe>(`/recipes/${id}/publish`, {
    method: "POST",
  });
}

/**
 * Get the authenticated user's own recipes (drafts included unless filtered).
 */
export async function getMyRecipes(
  status?: "draft" | "published",
): Promise<RecipesResponse> {
  const params = new URLSearchParams({ mine: "true", limit: "100" });
  if (status) params.set("status", status);
  return apiFetch(`/recipes?${params.toString()}`);
}

/**
 * Get published-catalog recipe counts per dietary tag (#69).
 */
export async function getTagCounts(tags: string[]): Promise<TagCountsResponse> {
  const params = new URLSearchParams({ tags: tags.join(",") });
  return apiFetch<TagCountsResponse>(
    `/recipes/tag-counts?${params.toString()}`,
  );
}
